import * as THREE from 'three';
import { LINE_NAMES, TICK_HZ } from '@somnhold/content';
import { BEAM_PLACE, beamLoad, cragNext, cragWarn, lineFull, linePagesOut, LINE_SETTLE, LINE_TAKE, lineRight, sendaGoal, stormLeft, STORM_BANNER, STORM_LIFT, STORM_ROCK, STORM_SHIELD, STORM_SLOT, STORM_STONE, type BattleState } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { clear, h, setStyle } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { pinMark } from './mark-pin.ts';

/** Where the pass's marks hang (render's PassPlace anchors, world points). */
export interface PassAnchors {
  slots: THREE.Vector3[];
  stretcher: THREE.Vector3 | null;
  ruy: THREE.Vector3 | null;
  stones: THREE.Vector3[];
  basket: THREE.Vector3 | null;
  rocks: THREE.Vector3[];
  pile: THREE.Vector3 | null;
  banner: THREE.Vector3 | null;
  prints: THREE.Vector3[];
  shield: THREE.Vector3 | null;
  niches: THREE.Vector3[];
}

/** An iron wedge, point down. */
const WEDGE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 1.5h8l-1.2 3H5.2z" fill="currentColor" opacity="0.7"/><path d="M5.2 5h5.6L8 15z" fill="currentColor"/></svg>';

type Kind = 'crag' | 'line' | 'beam' | 'storm';

/** A mark's ring fill (`--k`), written only when it changes (a style write a tick per mark showed in the profile). */
const setK = (m: HTMLElement, k: number): void => {
  const v = k.toFixed(2);
  if (m.dataset.k === v) return;
  m.dataset.k = v;
  m.style.setProperty('--k', v);
};

/**
 * The Pass of the Rift on screen (docs/sendas/guerrero.md, chapter 5), inside a path's own plate (game/senda-hud.ts),
 * short enough that a phone's plate stays under a quarter of the screen, and over the board:
 *
 * - the ledge of the wind: Harl up the ledge; the wind's count, red and pulsing when it warns («¡Viento! A un hueco de
 *   la pared»); the niches marked on the board while it warns.
 * - the camp and Ruy's line: the camp's nightmares left; then how many stand right (not which), the shield in hand, the
 *   names on the stretcher (tap one to take it), the diary's pages as they come; over the board each slot, its number
 *   and the name set in it (tap: set or take), the fire's end and the snow's; «Así queda» once all ten are in.
 * - the beam: the number on the beam, the basket's weight and its window, the snow; over the board the three heaps of
 *   stones (1, 2, 3) and the basket's four places (tap to set, take or swap).
 * - the Ventisquero: its life, how far from the line, the next charge's count; the wedges in hand and the pile; over the
 *   board its two footprints («Clavá una cuña»), the avalanche's three rocks while it is called, the beam's stone in
 *   its breath, Harl's shield and the gap, the banner and anyone to lift.
 *
 * Plain DOM written only when it changes (docs/RENDIMIENTO.md).
 */
export class PassHud {
  readonly panel: HTMLElement;
  private kind: Kind;
  private v = new THREE.Vector3();
  private key = '';
  // Shared rows.
  private barFill = h('i');
  private barN = h('b.bh-n');
  private note = h('p.bh-note');
  private status = h('span.ps-status');
  // The ledge.
  private nicheTags: HTMLElement[] = [];
  // The line.
  private rightPips: HTMLElement[] = [];
  private rightN = h('b.bh-n');
  private handEl = h('span.ps-hand');
  private names: HTMLButtonElement[] = [];
  private pages = h('ol.ps-pages');
  private pageWait = h('small.ps-wait');
  private settle: HTMLButtonElement;
  private slotMarks: HTMLButtonElement[] = [];
  private ends: HTMLElement[] = [];
  private campRow = h('div.bh-row');
  private lineRows = h('div.ps-line');
  // The beam.
  private num = h('b.ps-num');
  private load = h('b.ps-num');
  private window = h('small.ps-wait');
  private snowFill = h('i');
  private stoneMarks: HTMLButtonElement[] = [];
  private placeMarks: HTMLButtonElement[] = [];
  // The Ventisquero.
  private hpFill = h('i');
  private left = h('b.bh-n');
  private charge = h('span.ps-status');
  private hand: HTMLElement[] = [];
  private pileN = h('small.ps-wait');
  private printMarks: HTMLButtonElement[] = [];
  private rockMarks: HTMLButtonElement[] = [];
  private stoneMark: HTMLButtonElement;
  private shieldMark: HTMLButtonElement;
  private slotMark: HTMLButtonElement;
  private bannerMark: HTMLButtonElement;
  private liftMarks: HTMLButtonElement[] = [];

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
    private who: Record<string, string>,
  ) {
    const r = s.rules?.senda;
    this.kind = r?.crag ? 'crag' : r?.line ? 'line' : r?.beam ? 'beam' : 'storm';
    const btn = (cls: string, test: string, at: number, ...kids: (HTMLElement | string)[]): HTMLButtonElement =>
      h(`button.${cls}`, { type: 'button', 'data-test': test, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)) }, ...kids) as HTMLButtonElement;
    this.settle = btn('btn.ps-settle', 'pass-settle', LINE_SETTLE, t('senda.pass.settle'));
    this.stoneMark = btn('ps-mark.wide', 'pass-stone', STORM_STONE, h('span.ps-ring'), h('b', {}, t('senda.pass.stoneMark')));
    this.shieldMark = btn('ps-mark.wide', 'pass-shield', STORM_SHIELD, h('span.ps-ring'), h('b', {}, t('senda.pass.shieldMark')));
    this.slotMark = btn('ps-mark.wide', 'pass-gap', STORM_SLOT, h('span.ps-ring'), h('b', {}, t('senda.pass.slotMark')));
    this.bannerMark = btn('ps-mark.wide', 'pass-banner', STORM_BANNER, h('span.ps-ring'), h('b', {}, t('senda.pass.banner')));
    if (this.kind === 'crag') this.panel = this.buildCrag(s);
    else if (this.kind === 'line') this.panel = this.buildLine(s, btn);
    else if (this.kind === 'beam') this.panel = this.buildBeam(s, btn);
    else this.panel = this.buildStorm(s, btn);
  }

  private bar(label: string, ico: string, fill: HTMLElement, n: HTMLElement): HTMLElement {
    return h('div.bh-row', {}, h('span.bh-ico', { html: ico }), h('span.bh-label', {}, label), h('div.bar.bh-bar', {}, fill), n);
  }

  /* ---------- the ledge of the wind ---------- */

  private buildCrag(s: BattleState): HTMLElement {
    const n = s.rules?.senda?.crag ? 4 : 0;
    this.nicheTags = Array.from({ length: 12 }, () => {
      const el = h('span.ps-tag', {}, t('senda.pass.niche'));
      el.style.display = 'none';
      this.board.append(el);
      return el;
    });
    void n;
    return h('div.bh.ps-crag', {}, this.bar(t('senda.pass.up'), ICONS.walk, this.barFill, this.barN), h('div.bh-row.ps-wind', {}, h('span.bh-ico', { html: ICONS.rime }), this.status));
  }

  private updateCrag(s: BattleState): void {
    const g = sendaGoal(s);
    const k = Math.max(0, Math.min(1, g.done / Math.max(1, g.need)));
    setStyle(this.barFill, 'width', `${(k * 100).toFixed(1)}%`);
    const pct = `${Math.floor(k * 100)} %`;
    if (this.barN.textContent !== pct) this.barN.textContent = pct;
    const r = s.rules?.senda?.crag;
    const w = s.senda?.crag;
    if (!r || !w) return;
    const warn = cragWarn(s, r, w);
    const left = cragNext(s, r, w);
    const text = warn ? t('senda.pass.gust') : left >= 0 ? t('senda.pass.gustIn', { s: Math.ceil(Math.max(0, left - r.warn) / TICK_HZ) }) : '';
    if (this.status.textContent !== text) this.status.textContent = text;
    this.status.parentElement?.classList.toggle('warn', warn);
  }

  /* ---------- the camp and Ruy's line ---------- */

  private buildLine(s: BattleState, btn: (cls: string, test: string, at: number, ...kids: (HTMLElement | string)[]) => HTMLButtonElement): HTMLElement {
    const r = s.rules?.senda?.line;
    this.rightPips = Array.from({ length: 10 }, () => h('i'));
    this.names = LINE_NAMES.map((n, i) => btn('ps-name', `pass-name-${n}`, LINE_TAKE + i, t(`senda.pass.name.${n}`)));
    this.slotMarks = (r?.slots ?? []).map((_, i) =>
      btn(`ps-slot${i === 5 ? '.harl' : ''}`, `pass-slot-${i + 1}`, i, h('span.ps-ring'), h('b.ps-sn', {}, String(i + 1)), h('small.ps-who')),
    );
    this.slotMarks.forEach((m, i) => {
      if (i === 5) {
        m.disabled = true;
        const who = m.querySelector('.ps-who');
        if (who) who.textContent = this.who.harlOld ?? 'Harl';
      }
      this.board.append(m);
    });
    this.ends = [h('span.ps-end.fire', {}, t('senda.pass.fire')), h('span.ps-end', {}, t('senda.pass.snow'))];
    for (const e of this.ends) this.board.append(e);
    this.campRow.append(h('span.bh-ico', { html: ICONS.skull }), h('span.bh-label', {}, t('senda.pass.camp')), this.status);
    this.lineRows.append(
      h('div.bh-row', {}, h('span.bh-label', {}, t('senda.pass.right')), h('span.ps-pips', {}, ...this.rightPips), this.rightN, this.settle),
      h('div.bh-row', {}, h('span.bh-label', {}, t('senda.pass.hand')), this.handEl),
      h('div.ps-names', {}, ...this.names),
      h('div.ps-pagebox', {}, h('div.ps-pagehead', {}, h('span', { html: ICONS.target }), t('senda.pass.pages'), this.pageWait), this.pages),
    );
    this.lineRows.hidden = true;
    return h('div.bh.ps-linep', {}, this.campRow, this.lineRows);
  }

  private updateLine(s: BattleState): void {
    const w = s.senda?.line;
    const r = s.rules?.senda?.line;
    if (!w || !r) return;
    const laying = w.phase >= 2;
    this.campRow.hidden = laying;
    this.lineRows.hidden = !laying;
    if (!laying) {
      const n = s.enemies.filter((e) => e.hp > 0).length;
      const text = t('senda.pass.campLeft', { n });
      if (this.status.textContent !== text) this.status.textContent = text;
      this.slotMarks.forEach((m) => (m.hidden = true));
      for (const e of this.ends) e.hidden = true;
      return;
    }
    const out = linePagesOut(s);
    const key = `${w.on.join(',')}|${w.pile.join('')}|${w.hand}|${w.at}|${out}|${w.phase}`;
    if (key !== this.key) {
      this.key = key;
      const right = lineRight(w.on);
      this.rightPips.forEach((p, i) => p.classList.toggle('on', i < right));
      this.rightN.textContent = t('senda.pass.rightN', { n: right });
      this.lineRows.classList.toggle('red', w.right);
      clear(this.handEl);
      this.handEl.textContent = w.hand >= 0 ? t(`senda.pass.name.${LINE_NAMES[w.hand]}`) : '—';
      this.names.forEach((b, i) => {
        b.hidden = !w.pile[i];
        b.classList.toggle('sent', w.at === LINE_TAKE + i);
        b.classList.toggle('stone', w.pile[i] === 2);
        b.disabled = s.result !== 'running';
      });
      this.slotMarks.forEach((m, i) => {
        m.hidden = false;
        if (i === 5) return;
        const who = w.on[i] as number;
        const lbl = m.querySelector('.ps-who');
        if (lbl) lbl.textContent = who >= 0 ? t(`senda.pass.name.${LINE_NAMES[who]}`) : '';
        m.classList.toggle('full', who >= 0);
        m.classList.toggle('sent', w.at === i);
        m.classList.toggle('red', w.right);
        m.disabled = s.result !== 'running' || w.phase === 3;
      });
      for (const e of this.ends) e.hidden = false;
      this.settle.hidden = !lineFull(w) || w.right || w.phase === 3;
      // The pages out so far, the newest first.
      clear(this.pages);
      for (let i = out - 1; i >= 0; i--) this.pages.append(h('li', {}, t(`senda.pass.page.${r.pages[i]}`)));
    }
    const next = out < r.pages.length ? Math.max(0, Math.ceil((w.t1 + out * r.page - s.tick) / TICK_HZ)) : -1;
    const wait = next >= 0 ? t('senda.pass.pageWait', { s: next }) : '';
    if (this.pageWait.textContent !== wait) this.pageWait.textContent = wait;
    const k = w.at >= 0 ? Math.min(1, w.held / Math.max(1, r.put)) : 0;
    for (const m of this.slotMarks) setK(m, m.classList.contains('sent') ? k : 0);
  }

  /* ---------- the beam ---------- */

  private buildBeam(s: BattleState, btn: (cls: string, test: string, at: number, ...kids: (HTMLElement | string)[]) => HTMLButtonElement): HTMLElement {
    const r = s.rules?.senda?.beam;
    this.stoneMarks = (r?.values ?? [1, 2, 3]).map((v, i) => btn('ps-mark', `pass-heap-${v}`, i, h('span.ps-ring'), h('b', {}, String(v))));
    this.placeMarks = Array.from({ length: r?.slots ?? 4 }, (_, i) => btn('ps-mark.place', `pass-place-${i}`, BEAM_PLACE + i, h('span.ps-ring'), h('b', {}, '·')));
    for (const m of [...this.stoneMarks, ...this.placeMarks]) this.board.append(m);
    return h(
      'div.bh.ps-beam',
      {},
      h('div.bh-row', {}, h('span.bh-label', {}, t('senda.pass.beamNum')), this.num, h('span.bh-label.ps-sep', {}, t('senda.pass.basket')), this.load, this.window),
      h('div.bh-row', {}, h('span.bh-ico', { html: ICONS.rime }), h('span.bh-label', {}, t('senda.pass.snowOn')), h('div.bar.bh-bar', {}, this.snowFill)),
      h('div.bh-row', {}, h('span.bh-label', {}, t('senda.pass.hand')), this.handEl, this.status),
    );
  }

  private updateBeam(s: BattleState): void {
    const w = s.senda?.beam;
    const r = s.rules?.senda?.beam;
    if (!w || !r) return;
    const load = beamLoad(w);
    const key = `${w.num}|${w.basket.join('')}|${w.hand}|${w.at}|${w.fallen}|${w.calm}|${w.creak > 0}|${s.senda?.nums.beam ?? -1}`;
    if (key !== this.key) {
      this.key = key;
      this.num.textContent = String(w.num);
      this.load.textContent = String(load);
      this.window.textContent = t('senda.pass.window', { a: Math.max(0, w.num - r.gap), b: w.num - 1 });
      const bad = !w.fallen && (load >= w.num || load < w.num - r.gap);
      this.load.classList.toggle('bad', bad);
      this.load.classList.toggle('good', !w.fallen && load === w.num - 1);
      this.handEl.textContent = w.hand ? String(w.hand) : '—';
      const status = w.fallen ? t('senda.pass.fallen') : w.calm ? (s.senda?.nums.beam === 1 ? t('senda.pass.ready') : t('senda.pass.calm')) : w.creak > 0 ? t('senda.pass.creak') : '';
      this.status.textContent = status;
      this.status.classList.toggle('bad', w.fallen || w.creak > 0);
      this.placeMarks.forEach((m, i) => {
        const v = w.basket[i] ?? 0;
        const b = m.querySelector('b');
        if (b) b.textContent = v ? String(v) : '·';
        m.classList.toggle('full', v > 0);
        m.classList.toggle('sent', w.at === BEAM_PLACE + i);
        m.disabled = s.result !== 'running' || w.fallen;
      });
      this.stoneMarks.forEach((m, i) => {
        m.classList.toggle('sent', w.at === i);
        m.classList.toggle('held', w.hand === r.values[i]);
        m.disabled = s.result !== 'running' || w.fallen;
      });
    }
    setStyle(this.snowFill, 'width', `${((100 * w.snow) / Math.max(1, (r.end - r.start) * r.per)).toFixed(1)}%`);
    const k = w.at >= 0 ? Math.min(1, w.held / Math.max(1, r.put)) : 0;
    for (const m of this.stoneMarks) setK(m, m.classList.contains('sent') ? k : 0);
    for (const m of this.placeMarks) setK(m, m.classList.contains('sent') ? k : 0);
  }

  /* ---------- the Ventisquero ---------- */

  private buildStorm(s: BattleState, btn: (cls: string, test: string, at: number, ...kids: (HTMLElement | string)[]) => HTMLButtonElement): HTMLElement {
    const r = s.rules?.senda?.storm;
    this.hand = Array.from({ length: r?.hand ?? 2 }, () => h('i', { html: WEDGE }));
    this.printMarks = [0, 1].map((i) => btn('ps-mark.print', `pass-print-${i}`, i, h('span.ps-ring'), h('span.ps-w', { html: WEDGE })));
    this.rockMarks = (r?.rocks.flat() ?? []).map((rk, i) => btn('ps-mark', `pass-rock-${i}`, STORM_ROCK + i, h('span.ps-ring'), h('b', {}, rk.dir > 0 ? '→' : '←')));
    const people = s.senda?.npcs ?? [];
    this.liftMarks = people.map((n, i) => btn('ps-mark.wide', `pass-lift-${n.id}`, STORM_LIFT + i, h('span.ps-ring'), h('b', {}, t('senda.pass.lift'))));
    for (const m of [...this.printMarks, ...this.rockMarks, this.stoneMark, this.shieldMark, this.slotMark, this.bannerMark, ...this.liftMarks]) {
      m.hidden = true;
      this.board.append(m);
    }
    return h(
      'div.bh.ps-storm',
      {},
      h('div.bh-row', {}, h('span.bh-ico', { html: ICONS.skull }), h('span.bh-label', {}, t('senda.pass.storm')), h('div.bar.bh-bar.fh-hp', {}, this.hpFill)),
      h('div.bh-row', {}, h('span.bh-label', {}, t('senda.pass.left')), this.left, this.charge),
      h('div.bh-row', {}, h('span.bh-label', {}, t('senda.pass.wedges')), h('span.sl-hand', {}, ...this.hand), this.pileN),
      this.note,
    );
  }

  private updateStorm(s: BattleState): void {
    const w = s.senda?.storm;
    const r = s.rules?.senda?.storm;
    if (!w || !r) return;
    const e = s.enemies.find((x) => x.id === w.boss);
    setStyle(this.hpFill, 'width', `${e ? ((100 * Math.max(0, e.hp)) / e.maxHp).toFixed(1) : 0}%`);
    const left = Math.max(0, stormLeft(r, w));
    const hex = (left / 866).toFixed(1);
    const lt = t('senda.pass.leftN', { n: hex });
    if (this.left.textContent !== lt) this.left.textContent = lt;
    this.left.classList.toggle('bad', left < 3 * 866);
    const exposed = s.tick < w.exposed;
    const ct = exposed ? t('senda.pass.exposed') : w.broken ? '' : t('senda.pass.chargeIn', { s: Math.max(0, Math.ceil((w.next - s.tick) / TICK_HZ)) });
    if (this.charge.textContent !== ct) this.charge.textContent = ct;
    this.charge.classList.toggle('good', exposed);
    this.charge.classList.toggle('bad', !exposed && w.next - s.tick < 4 * TICK_HZ);
    this.hand.forEach((el, i) => el.classList.toggle('on', i < w.hand));
    const pt = t('senda.pass.pile', { n: w.pile });
    if (this.pileN.textContent !== pt) this.pileN.textContent = pt;
    const note = w.closed ? t('senda.pass.closed') : w.shield >= 1 ? t('senda.pass.shield') : w.breath > 0 ? (r.ready && !w.stone ? t('senda.pass.breath') : t('senda.pass.breathNo')) : w.fall >= 0 && !w.fell ? t('senda.pass.callIn', { s: Math.max(0, Math.ceil((w.fall - s.tick) / TICK_HZ)) }) : '';
    if (this.note.textContent !== note) this.note.textContent = note;
    const running = s.result === 'running';
    this.printMarks.forEach((m, i) => {
      m.hidden = !running || !w.prints[i] || w.hand <= 0 || w.wedged >= 0 || w.broken;
      m.classList.toggle('sent', w.at === i);
      setK(m, w.at === i ? Math.min(1, w.held / Math.max(1, r.stand)) : 0);
    });
    this.rockMarks.forEach((m, i) => {
      m.hidden = !running || w.fall < 0 || w.fell;
      m.classList.toggle('shut', w.shut[i] === 1);
      m.classList.toggle('sent', w.at === STORM_ROCK + i);
      setK(m, w.at === STORM_ROCK + i ? Math.min(1, w.held / Math.max(1, r.stand)) : 0);
    });
    this.stoneMark.hidden = !running || !(w.breath > 0 && r.ready && !w.stone);
    this.shieldMark.hidden = !running || w.shield !== 1;
    this.slotMark.hidden = !running || w.shield !== 2;
    this.bannerMark.hidden = !running || w.banner;
    this.liftMarks.forEach((m, i) => {
      const n = s.senda?.npcs[i];
      m.hidden = !running || !n || n.away || n.hp > 0 || !(n.id === 'wenn' || n.id === 'dalmo');
    });
  }

  update(s: BattleState): void {
    if (this.kind === 'crag') this.updateCrag(s);
    else if (this.kind === 'line') this.updateLine(s);
    else if (this.kind === 'beam') this.updateBeam(s);
    else this.updateStorm(s);
  }

  /** A mark over a world point (hidden off screen); `dy` lifts it on the screen (px). */
  private pin(el: HTMLElement, p: THREE.Vector3 | null | undefined, camera: THREE.Camera, w: number, ht: number, dy = 0): void {
    if (!p || el.hidden) return setStyle(el, 'display', 'none');
    this.v.copy(p).project(camera);
    if (this.v.z > 1) return setStyle(el, 'display', 'none');
    setStyle(el, 'display', '');
    pinMark(el, (this.v.x * 0.5 + 0.5) * w, (-this.v.y * 0.5 + 0.5) * ht - dy);
  }

  place(s: BattleState, camera: THREE.Camera, w: number, ht: number, a: PassAnchors | null, at: Map<string, THREE.Vector3>): void {
    if (!a) return;
    if (this.kind === 'crag') {
      const r = s.rules?.senda?.crag;
      const cw = s.senda?.crag;
      const warn = !!r && !!cw && cragWarn(s, r, cw);
      this.nicheTags.forEach((el, i) => {
        const p = a.niches[i];
        if (!warn || !p) return setStyle(el, 'display', 'none');
        this.pin(el, p, camera, w, ht);
      });
      return;
    }
    if (this.kind === 'line') {
      this.slotMarks.forEach((m, i) => this.pin(m, a.slots[i], camera, w, ht));
      const s0 = a.slots[0];
      const s10 = a.slots[10];
      if (this.ends[0] && s0) this.pin(this.ends[0], s0.clone().setY(s0.y - 0.6).add(new THREE.Vector3(Math.sign(s0.x) * 0.9, 0, 0.6)), camera, w, ht);
      if (this.ends[1] && s10) this.pin(this.ends[1], s10.clone().setY(s10.y - 0.6).add(new THREE.Vector3(Math.sign(s10.x) * 0.9, 0, 0.6)), camera, w, ht);
      return;
    }
    if (this.kind === 'beam') {
      this.stoneMarks.forEach((m, i) => this.pin(m, a.stones[i], camera, w, ht));
      const b = a.basket;
      this.placeMarks.forEach((m, i) => this.pin(m, b ? b.clone().add(new THREE.Vector3(((i % 2) - 0.5) * 0.9, i < 2 ? 0.45 : -0.25, 0)) : null, camera, w, ht));
      return;
    }
    this.printMarks.forEach((m, i) => this.pin(m, a.prints[i], camera, w, ht));
    this.rockMarks.forEach((m, i) => this.pin(m, a.rocks[i], camera, w, ht));
    this.pin(this.stoneMark, a.basket, camera, w, ht);
    this.pin(this.shieldMark, a.shield, camera, w, ht);
    this.pin(this.slotMark, a.slots[5], camera, w, ht);
    this.pin(this.bannerMark, a.banner, camera, w, ht);
    const people = s.senda?.npcs ?? [];
    this.liftMarks.forEach((m, i) => {
      const n = people[i];
      const p = n ? at.get(n.id) : undefined;
      this.pin(m, p ? this.v.copy(p).setY(p.y + 1.2).clone() : null, camera, w, ht);
    });
  }
}
